import React, { useState, useEffect } from 'react';
import './DashboardCard.css';

/**
 * DashboardCard component with hover effects and tooltip
 * 
 * @param {string} title - Card title
 * @param {string} description - Card description text
 * @param {string} buttonText - Text for the card button
 * @param {function} buttonAction - Function to execute on button click
 * @param {string} tooltipText - Text to show in the tooltip
 * @param {string} icon - Optional icon name for the card
 * @param {number} count - Optional count number to display (e.g. "3" for subscribed creators)
 * @param {array} items - Optional array of items to display in a list
 */
const DashboardCard = ({
  title,
  description,
  buttonText,
  buttonAction,
  tooltipText,
  icon,
  count,
  items,
  index = 0
}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Animation effect for card entrance
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100 + (index * 100)); // Staggered animation based on index
    
    return () => clearTimeout(timer);
  }, [index]);

  // Generate dynamic title with count if provided
  const displayTitle = count !== undefined ? `${title} (${count})` : title;

  return (
    <div 
      className={`dashboard-card ${isVisible ? 'visible' : ''}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'translateY(20px)',
        transition: 'opacity 0.5s ease, transform 0.3s ease-in-out'
      }}
    >
      <div className="card-content">
        <div className="card-header">
          {icon && <div className="card-icon">{icon}</div>}
          <h3>{displayTitle}</h3>
        </div>
        
        <p className="card-description">{description}</p>
        
        {items && items.length > 0 && (
          <ul className="card-items">
            {items.map((item, idx) => (
              <li key={item.id || idx}>
                {item.title} {item.creator && <span className="creator-name">by {item.creator}</span>}
              </li>
            ))}
          </ul>
        )}
        
        <button 
          className="card-button" 
          onClick={buttonAction}
        >
          {buttonText}
        </button>
      </div>
      
      {tooltipText && (
        <div className="card-tooltip">
          <div className="tooltip-content">
            <span className="tooltip-icon">💡</span>
            <span>{tooltipText}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardCard;
